import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { editionService } from "../services/editionService";
import {
  EditionValuesInit,
  getEditionCreateVariables,
  getEditionValues,
  EditionValuesType,
  EditionErrorsType,
  editionDisplayId,
} from "./EditionCommonStore";
import { Edition, SortOrder } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getEditionTypeValues,
  EditionTypeValuesType,
} from "../../editionType/store/EditionTypeCommonStore";
import { isNil } from "../../../common/utils";
import {
  getMusicalWorkValues,
  MusicalWorkValuesType,
} from "../../musicalWork/store/MusicalWorkCommonStore";
import { GridColumn } from "../../common/types";
import { UserAttachmentEditionCreateStore } from "../../userAttachmentEdition/store";
import { GridChangeSet } from "../../common/types";
import { EditionContentType } from "../../../api/graphql";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { editionTypeService } from "../../editionType/services/editionTypeService";
import { musicalWorkService } from "../../musicalWork/services/musicalWorkService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class EditionCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: EditionErrorsType = {};
  service = editionService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    editionWorkflow: { open: true, errors: [] },
    editionData: { open: true, errors: [] },
    contracts: { open: true, errors: [] },
    editionContracts: { open: true, errors: [] },
    editionDocumentation: { open: true, errors: [] },
    editionUserAttachments: { open: true, errors: [] },
    systemDates: { open: false, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: Edition | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = EditionValuesInit();
  nameError: string | undefined;
  editionTypeError: string | undefined;
  editionTypeService = editionTypeService;
  editionTypeSearchValue: string = "";
  editionTypeOptions: EditionTypeValuesType[] = [];
  contentTypeError: string | undefined;
  publicationLinkError: string | undefined;
  priceError: string | undefined;
  worksError: string | undefined;
  worksService = musicalWorkService;
  worksSearchValue: string = "";
  worksOptions: MusicalWorkValuesType[] = [];
  notesError: string | undefined;
  contractObservationError: string | undefined;
  documentationError: string | undefined;
  documentationVisibleColumns: GridColumn[];
  associatedDocumentsError: string | undefined;
  associatedDocumentsVisibleColumns: GridColumn[];
  userAttachmentsError: string | undefined;
  userAttachmentsVisibleColumns: GridColumn[];
  userAttachmentsCreateStore: UserAttachmentEditionCreateStore;
  createdAt_Error: string | undefined;
  lastUpdatedAt_Error: string | undefined;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<EditionValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.documentationVisibleColumns = [
      {
        name: "finalDocumentPdf",
        title: this.langStore.EditionDocumentationFinalDocumentPdfLabel,
      },
      {
        name: "documentName",
        title: this.langStore.EditionDocumentationDocumentNameLabel,
      },
    ];
    this.associatedDocumentsVisibleColumns = [
      {
        name: "file",
        title: this.langStore.AssociatedDocumentEditionFileLabel,
      },
    ];
    this.userAttachmentsVisibleColumns = [
      { name: "file", title: this.langStore.UserAttachmentEditionFileLabel },
    ];
    this.userAttachmentsCreateStore = new UserAttachmentEditionCreateStore(
      rootStore,
      "userAttachments_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<EditionValuesType>);
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<EditionValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return editionDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EditionSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  viewFormWorks(id: string) {
    if (id) {
      this.rootStore.openOrPushOverlay("musicalWork", id, "view");
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity
      if (params.callerData?.entityName === "UserAttachmentEdition") {
        this.userAttachmentsCreateStore.isWaitingForCallback = false;
        this.userAttachmentsCreateStore.callbackReturnPath = null;
        yield this.userAttachmentsCreateStore.returnedData(params);
        return;
      }

      if (params.callerData?.fieldName === "editionType") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showEditionType = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.editionTypeOne({
            id: params.returnData.resultId,
          });
          const editionType = result.editionTypeOne;
          this.entityValues.showEditionType = {
            displayId: editionType.displayId,
            id: editionType.id,
          };
          this.setEditionTypeOptions([
            getEditionTypeValues(editionType, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "works") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showWorks.push({
            displayId,
            id: params.returnData.entity.id,
          });
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.musicalWorkOne({
            id: params.returnData.resultId,
          });
          const musicalWork = result.musicalWorkOne;
          this.entityValues.showWorks.push({
            displayId: musicalWork.displayId,
            id: musicalWork.id,
          });
          this.setWorksOptions([
            getMusicalWorkValues(musicalWork, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "editionData",
        label: this.rootStore.langStore.groupEditionEditionDataLabel,
        errors: this.getCardErrors("editionData"),
      },
      {
        id: "contracts",
        label: this.rootStore.langStore.groupEditionContractsLabel,
        errors: this.getCardErrors("contracts"),
      },
      {
        id: "editionContracts",
        label: this.rootStore.langStore.groupEditionEditionContractsLabel,
        errors: this.getCardErrors("editionContracts"),
      },
      {
        id: "editionDocumentation",
        label: this.rootStore.langStore.groupEditionEditionDocumentationLabel,
        errors: this.getCardErrors("editionDocumentation"),
      },
      {
        id: "editionUserAttachments",
        label: this.rootStore.langStore.groupEditionEditionUserAttachmentsLabel,
        errors: this.getCardErrors("editionUserAttachments"),
      },
      {
        id: "systemDates",
        label: this.rootStore.langStore.groupEditionSystemDatesLabel,
        errors: this.getCardErrors("systemDates"),
      },
    ];
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  setEditionTypeOptions(values: EditionTypeValuesType[]) {
    this.editionTypeOptions = values;
  }

  *loadEditionTypeValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("EditionType");
    const allOptions = cache.getOptions(
      "EditionType"
    ) as EditionTypeValuesType[];

    const searchValue = this.editionTypeSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setEditionTypeOptions(filtered);
  }

  getEditionTypeOptions() {
    return this.editionTypeOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setEditionTypeValue(value: string) {
    this.editionTypeSearchValue = value;
    yield this.loadEditionTypeValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedEditionType(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewEditionType(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "editionType"
      );
    } else {
      if (value) {
        this.entityValues.showEditionType = value;
        this.entityValues.__editionType = value.displayId;
      }
    }
  }

  getContentTypeValue(): EditionContentType {
    return this.entityValues.contentType;
  }

  setContentTypeValue(value: EditionContentType) {
    this.entityValues.contentType = value;
  }

  getContentTypeOptions() {
    return this.langStore.EditionContentTypeOptions;
  }

  getPublicationLinkValue(): string {
    return this.entityValues.publicationLink;
  }

  setPublicationLinkValue(value: string) {
    this.entityValues.publicationLink = value;
  }

  getPriceValue(): number {
    return this.entityValues.price;
  }

  setPriceValue(value: number) {
    this.entityValues.price = value;
  }

  setWorksOptions(values: MusicalWorkValuesType[]) {
    this.worksOptions = values;
  }

  *loadWorksValues(): any {
    let where: any = null;
    if (this.worksSearchValue) {
      const searchFilter = {
        title: { contains: this.worksSearchValue, mode: "insensitive" },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.musicalWorkListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ lastUpdatedAt_: SortOrder.Desc }],
    });
    this.setWorksOptions(
      (result.musicalWorkListWithOffset || []).map((x: any) =>
        getMusicalWorkValues(x, this.langStore)
      )
    );
  }

  getWorksOptions() {
    return this.worksOptions.map((x: any) => ({
      ...x,
      displayId: resolveCurrentLang(x.displayId, this.langStore),
    }));
  }

  *setWorksValue(value: string) {
    this.worksSearchValue = value;
    yield this.loadWorksValues();
  }

  setSelectedWorks(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewMusicalWork(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "works"
      );
    } else {
      this.entityValues.showWorks = values;
      this.entityValues.__works = values.map((v) => v.displayId);
    }
  }

  getNotesValue(): string {
    return this.entityValues.notes;
  }

  setNotesValue(value: string) {
    this.entityValues.notes = value;
  }

  getContractObservationValue(): string {
    return this.entityValues.contractObservation;
  }

  setContractObservationValue(value: string) {
    this.entityValues.contractObservation = value;
  }

  userAttachmentsLoadRow(row: any) {
    this.userAttachmentsCreateStore.loadValuesFromRow(row);
  }

  userAttachmentsRestore(id: string) {
    const userAttachments = this.entityValues.showUserAttachments.filter(
      (x) => x.id === id
    )[0];
    if (userAttachments) {
      userAttachments.__status__ = restoredStatus(userAttachments.__status__);
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  userAttachmentsValidateChanges(_changes: GridChangeSet) {
    const errors = this.userAttachmentsCreateStore.validateData(
      this.userAttachmentsCreateStore.entityValues
    );

    this.userAttachmentsCreateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  userAttachmentsCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.userAttachmentsCreateStore.entityValues;
      const userAttachments = this.entityValues.showUserAttachments;
      for (
        let index = 0;
        index < this.entityValues.showUserAttachments.length;
        index++
      ) {
        if (userAttachments[index].id === values.id) {
          userAttachments[index] = {
            ...values,
            __status__: updatedStatus(values.__status__),
          };
          break;
        }
      }
      this.userAttachmentsCreateStore.clearFields();
    } else if (changes.added) {
      const values = this.userAttachmentsCreateStore.entityValues;
      if (!this.entityValues.showUserAttachments) {
        this.entityValues.showUserAttachments = [];
      }
      this.entityValues.showUserAttachments = [
        ...this.entityValues.showUserAttachments,
        {
          ...values,
          id: `${this.uniqueCounter++}`,
          __status__: "added",
        },
      ];
      this.userAttachmentsCreateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const userAttachments = this.entityValues.showUserAttachments;
      for (
        let index = 0;
        index < this.entityValues.showUserAttachments.length;
        index++
      ) {
        if (userAttachments[index].id === id) {
          if (isOriginalCrudStatus(userAttachments[index].__status__)) {
            userAttachments[index] = {
              ...userAttachments[index],
              __status__: deleteStatus(userAttachments[index].__status__),
            };
          } else {
            this.entityValues.showUserAttachments =
              this.entityValues.showUserAttachments.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.userAttachments);
    //   this.setCRUDRowsFromValues(newChanges, this.userAttachmentsCreateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.userAttachments = getNewCRUDRows(newChanges, this.entityValues.userAttachments, this.uniqueCounter++);
    // }
  }

  getCreatedAt_Value(): Date | null {
    return this.entityValues.createdAt_;
  }

  setCreatedAt_Value(value: Date | null) {
    if (value !== null) {
      this.entityValues.createdAt_ = value;
    }
  }

  getLastUpdatedAt_Value(): Date | null {
    return this.entityValues.lastUpdatedAt_;
  }

  setLastUpdatedAt_Value(value: Date | null) {
    if (value !== null) {
      this.entityValues.lastUpdatedAt_ = value;
    }
  }

  addNewEditionType(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Edition",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/editionType/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewMusicalWork(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Edition",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/musicalWork/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = EditionValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      editionWorkflow: { open: true, errors: [] },
      editionData: { open: true, errors: [] },
      contracts: { open: true, errors: [] },
      editionContracts: { open: true, errors: [] },
      editionDocumentation: { open: true, errors: [] },
      editionUserAttachments: { open: true, errors: [] },
      systemDates: { open: false, errors: [] },
    };
    this.editionTypeSearchValue = "";
    this.editionTypeOptions = [];
    this.worksSearchValue = "";
    this.worksOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: EditionValuesType): EditionErrorsType {
    const errors: EditionErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EditionNameLabel,
      };
      this.setCardError("editionData", errors.name.message);
    }
    this.editionTypeError = undefined;
    if (values.showEditionType === null || values.showEditionType.id === "") {
      errors.editionType = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EditionEditionTypeLabel,
      };
      this.setCardError("editionData", errors.editionType.message);
    }

    if (!values.contentType) {
      errors.contentType = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EditionContentTypeLabel,
      };
      this.setCardError("editionData", errors.contentType.message);
    }

    if (isNil(values.price)) {
      errors.price = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EditionPriceLabel,
      };
      this.setCardError("editionData", errors.price.message);
    }
    if (values.showWorks.length === 0) {
      errors.works = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.EditionWorksLabel,
      };
      this.setCardError("editionData", errors.works.message);
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "EditionCreateStore"
    );
  }

  *duplicatesEdition(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const editionInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (editionInstance) {
      this.entityValues = getEditionValues(
        editionInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la edición del registro creado.
      this.rootStore.router.replace(`/edition/update/${returnData.resultId}`);
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewEdition(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewEdition(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      contentType: this.entityValues.contentType,
      publicationLink: this.entityValues.publicationLink,
      price: this.entityValues.price,
      notes: this.entityValues.notes,
      contractObservation: this.entityValues.contractObservation,
      createdAt_: this.entityValues.createdAt_,
      lastUpdatedAt_: this.entityValues.lastUpdatedAt_,
      editionType: this.entityValues.showEditionType?.id || null,
      works: (this.entityValues.showWorks || []).map((x: any) => x.id).sort(),
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getEditionValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = EditionValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const variables = getEditionCreateVariables(this.entityValues);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.editionCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.editionCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.editionCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesEdition(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.name = initialDisplayId;
      }

      yield this.loadEditionTypeValues();
      yield this.loadWorksValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
